import { useState } from 'react'

import { AutostradaLogo } from './AutostradaLogo'
import styles from './FormWrapper.module.css'

import type { ReactNode } from 'react'

interface FormWrapperProps {
    children: ReactNode
    /** Показувати логотип над формою */
    showLogo?: boolean
    /** Заголовок картки (напр. «Вхід до системи») */
    title?: ReactNode
    /** Пояснення під заголовком */
    subtitle?: ReactNode
}

/** Кадр з відео: постер до старту відтворення і єдиний фон там, де відео не вантажимо */
const POSTER_URL = '/videos/hero-poster.jpg'

/**
 * Чи вмикати відео-фон.
 * На вузьких екранах (телефон водія, мобільний трафік) показуємо лише постер-картинку
 * замість відео, щоб не тягнути зайві мегабайти. prefers-reduced-motion свідомо не
 * враховуємо: у Windows цей прапорець вмикається вимкненими «Ефектами анімації», і без
 * відео сторінка виглядала б інакше, ніж задумано (рішення власника продукту).
 */
const shouldPlayVideo = (): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia('(min-width: 768px)').matches
}

/**
 * Обгортка публічних форм (логін, відновлення пароля, запрошення):
 * фон, картка з логотипом і заголовком, підпис унизу.
 */
export const FormWrapper = ({ children, showLogo = true, title, subtitle }: FormWrapperProps) => {
    // Рішення приймаємо один раз при монтуванні — перемикати відео при ресайзі не потрібно
    const [withVideo] = useState(shouldPlayVideo)

    return (
        <div className={styles['page']}>
            {/* Фон: відео з головної сторінки autostrada.ua або його кадр */}
            <div className={styles['videoBackground']} aria-hidden="true">
                {withVideo ? (
                    <video className={styles['video']} poster={POSTER_URL} autoPlay loop muted playsInline>
                        <source src="/videos/hero.mp4" type="video/mp4" />
                    </video>
                ) : (
                    <img className={styles['video']} src={POSTER_URL} alt="" decoding="async" />
                )}
            </div>
            {/* Затемнення поверх відео/градієнта, щоб форма читалась */}
            <div className={styles['overlay']} aria-hidden="true" />

            <div className={styles['card']}>
                {showLogo && (
                    <div className={styles['logo']}>
                        <AutostradaLogo className={styles['logoImage']} />
                    </div>
                )}

                {(title || subtitle) && (
                    <header className={styles['header']}>
                        {title && <h1 className={styles['title']}>{title}</h1>}
                        {subtitle && <p className={styles['subtitle']}>{subtitle}</p>}
                    </header>
                )}

                <div className={styles['body']}>{children}</div>
            </div>
        </div>
    )
}
